import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  EARNINGS_PATH,
  payoutSetupUnfinished,
  readBannerDismissed,
  showsEarnings,
  useEarningsSignpost,
  waitingAmount,
  writeBannerDismissed,
} from "@/lib/earnings/signpost";

/**
 * "Earnings" in the account menus (the email dropdown and the workspace avatar menu), for anyone with earnings or
 * in a split; a dot while payouts aren't set up. `icon`: lead with a wallet icon (menus whose items have icons).
 */
export const EarningsMenuItem = ({ icon = false }: { icon?: boolean }) => {
  const navigate = useNavigate();
  const earnings = useEarningsSignpost();
  if (!showsEarnings(earnings)) return null;
  const unfinished = payoutSetupUnfinished(earnings);
  return (
    <DropdownMenuItem onClick={() => navigate(EARNINGS_PATH)} data-testid="earnings-menu-item">
      {icon && <Wallet className="h-4 w-4 mr-2" aria-hidden="true" />}
      <span className="flex-1">Earnings</span>
      {unfinished && (
        <span className="ml-2 h-2 w-2 rounded-full bg-primary" aria-label="Payouts not set up" title="Payouts not set up" data-testid="earnings-menu-dot" />
      )}
    </DropdownMenuItem>
  );
};

/**
 * "You have £42.10 waiting — set up payouts": shown while money is held only because payouts aren't set up.
 * Dismissible for this session.
 */
export const EarningsWaitingBanner = ({ className }: { className?: string }) => {
  const navigate = useNavigate();
  const earnings = useEarningsSignpost();
  const [dismissed, setDismissed] = useState(readBannerDismissed);
  const amount = waitingAmount(earnings);
  if (!amount || dismissed) return null;
  return (
    <div
      role="status"
      data-testid="earnings-waiting-banner"
      className={cn("flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm", className)}
    >
      <Wallet className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <p className="min-w-0 flex-1">
        You have <strong>{amount}</strong> waiting — set up payouts so we can pay you.
      </p>
      <Button size="sm" onClick={() => navigate(EARNINGS_PATH)}>
        Set up payouts
      </Button>
      <button
        type="button"
        onClick={() => {
          writeBannerDismissed();
          setDismissed(true);
        }}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label="Dismiss"
        title="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
